/**
 * Contribution registry — aggregates dynamic contributions registered by active
 * extensions during `activate(ctx)`: sidebar panels, commands, status-bar items,
 * settings panels, menu providers, unit-config actions.
 *
 * The editor shell subscribes to this registry to render the aggregated set.
 */
import type {
    CommandContribution,
    ExtensionVirtualPackageContext,
    ExtensionVirtualPackageProvider,
    FloatingPanelContribution,
    MenuContext,
    MenuProvider,
    SettingsPanelProps,
    SidebarPanelContribution,
    StatusBarContribution,
    UnitConfigActionContribution,
    UnitConfigContext,
} from "@magic-md/extension-sdk";
import type { FC } from "react";

interface OwnedSidebarPanel {
    extensionId: string;
    panel: SidebarPanelContribution;
}
interface OwnedFloatingPanel {
    extensionId: string;
    panel: FloatingPanelContribution;
}
interface OwnedCommand {
    extensionId: string;
    command: CommandContribution;
}
interface OwnedStatusBar {
    extensionId: string;
    item: StatusBarContribution;
}
interface OwnedSettingsPanel {
    extensionId: string;
    component: FC<SettingsPanelProps>;
}
interface OwnedMenuProvider {
    extensionId: string;
    provider: MenuProvider;
}
interface OwnedUnitConfigAction {
    extensionId: string;
    action: UnitConfigActionContribution;
}
export interface OwnedVirtualPackageProvider {
    extensionId: string;
    provider: ExtensionVirtualPackageProvider;
}

const sidebarPanels = new Map<string, OwnedSidebarPanel>();
const floatingPanels = new Map<string, OwnedFloatingPanel>();
const commands = new Map<string, OwnedCommand>();
const statusBarItems = new Map<string, OwnedStatusBar>();
const settingsPanels = new Map<string, OwnedSettingsPanel>();
const menuProviders = new Set<OwnedMenuProvider>();
const unitConfigActions = new Map<string, OwnedUnitConfigAction>();
const virtualPackageProviders = new Map<string, OwnedVirtualPackageProvider>();

const listeners = new Set<() => void>();
let revision = 0;

function notify(): void {
    revision += 1;
    for (const fn of listeners) fn();
}

function key(extensionId: string, id: string): string {
    return `${extensionId}::${id}`;
}

// ── Registration (returns disposers) ──

export function addSidebarPanel(extensionId: string, panel: SidebarPanelContribution): () => void {
    const k = key(extensionId, panel.id);
    sidebarPanels.set(k, { extensionId, panel });
    notify();
    return () => {
        if (sidebarPanels.delete(k)) notify();
    };
}

export function addFloatingPanel(
    extensionId: string,
    panel: FloatingPanelContribution,
): () => void {
    const k = key(extensionId, panel.id);
    floatingPanels.set(k, { extensionId, panel });
    notify();
    return () => {
        if (floatingPanels.delete(k)) notify();
    };
}

export function addCommand(extensionId: string, command: CommandContribution): () => void {
    const k = key(extensionId, command.id);
    commands.set(k, { extensionId, command });
    notify();
    return () => {
        if (commands.delete(k)) notify();
    };
}

export function addStatusBarItem(extensionId: string, item: StatusBarContribution): () => void {
    const k = key(extensionId, item.id);
    statusBarItems.set(k, { extensionId, item });
    notify();
    return () => {
        if (statusBarItems.delete(k)) notify();
    };
}

export function addSettingsPanel(
    extensionId: string,
    component: FC<SettingsPanelProps>,
): () => void {
    settingsPanels.set(extensionId, { extensionId, component });
    notify();
    return () => {
        if (settingsPanels.delete(extensionId)) notify();
    };
}

export function addMenuProvider(extensionId: string, provider: MenuProvider): () => void {
    const owned: OwnedMenuProvider = { extensionId, provider };
    menuProviders.add(owned);
    notify();
    return () => {
        if (menuProviders.delete(owned)) notify();
    };
}

export function addUnitConfigAction(
    extensionId: string,
    action: UnitConfigActionContribution,
): () => void {
    const k = key(extensionId, action.id);
    unitConfigActions.set(k, { extensionId, action });
    notify();
    return () => {
        if (unitConfigActions.delete(k)) notify();
    };
}

export function addVirtualPackageProvider(
    extensionId: string,
    provider: ExtensionVirtualPackageProvider,
): () => void {
    const k = key(extensionId, provider.id);
    virtualPackageProviders.set(k, { extensionId, provider });
    notify();
    return () => {
        if (virtualPackageProviders.delete(k)) notify();
    };
}

/** Remove every contribution owned by one extension (on deactivate). */
export function removeAllForExtension(extensionId: string): void {
    let changed = false;
    for (const [k, v] of sidebarPanels) if (v.extensionId === extensionId && sidebarPanels.delete(k)) changed = true;
    for (const [k, v] of floatingPanels) if (v.extensionId === extensionId && floatingPanels.delete(k)) changed = true;
    for (const [k, v] of commands) if (v.extensionId === extensionId && commands.delete(k)) changed = true;
    for (const [k, v] of statusBarItems) if (v.extensionId === extensionId && statusBarItems.delete(k)) changed = true;
    if (settingsPanels.delete(extensionId)) changed = true;
    for (const v of [...menuProviders]) if (v.extensionId === extensionId && menuProviders.delete(v)) changed = true;
    for (const [k, v] of unitConfigActions) if (v.extensionId === extensionId && unitConfigActions.delete(k)) changed = true;
    for (const [k, v] of virtualPackageProviders) if (v.extensionId === extensionId && virtualPackageProviders.delete(k)) changed = true;
    if (changed) notify();
}

// ── Read API ──

export const contributionRegistry = {
    sidebarPanels(): OwnedSidebarPanel[] {
        return [...sidebarPanels.values()];
    },
    floatingPanels(): OwnedFloatingPanel[] {
        return [...floatingPanels.values()];
    },
    commands(): OwnedCommand[] {
        return [...commands.values()];
    },
    statusBarItems(): OwnedStatusBar[] {
        return [...statusBarItems.values()];
    },
    settingsPanel(extensionId: string): FC<SettingsPanelProps> | undefined {
        return settingsPanels.get(extensionId)?.component;
    },
    settingsPanels(): OwnedSettingsPanel[] {
        return [...settingsPanels.values()];
    },
    menuProviders(): OwnedMenuProvider[] {
        return [...menuProviders.values()];
    },
    virtualPackageProviders(): OwnedVirtualPackageProvider[] {
        return [...virtualPackageProviders.values()];
    },
    buildVirtualPackages(context: ExtensionVirtualPackageContext) {
        return [...virtualPackageProviders.values()]
            .filter(({ provider }) => !provider.when || provider.when(context))
            .flatMap(({ extensionId, provider }) =>
                provider.build(context).map((node) => ({
                    extensionId,
                    providerId: provider.id,
                    provider,
                    node,
                })),
            );
    },
    /** Build context menu items from every matching provider for a surface. */
    buildMenu(context: MenuContext) {
        const items = [];
        for (const { provider } of menuProviders) {
            if (provider.surface !== context.surface) continue;
            if (provider.when && !provider.when(context)) continue;
            items.push(...provider.build(context));
        }
        return items;
    },
    /** Build context menu items and keep their owning extension ids. */
    buildOwnedMenu(context: MenuContext) {
        const items = [];
        for (const { extensionId, provider } of menuProviders) {
            if (provider.surface !== context.surface) continue;
            if (provider.when && !provider.when(context)) continue;
            for (const item of provider.build(context)) {
                items.push({ extensionId, item });
            }
        }
        return items;
    },
    unitConfigActions(unit?: UnitConfigContext): OwnedUnitConfigAction[] {
        const all = [...unitConfigActions.values()];
        if (!unit) return all;
        return all.filter(({ action }) => !action.when || action.when(unit));
    },
    revision(): number {
        return revision;
    },
    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
    clear(): void {
        sidebarPanels.clear();
        floatingPanels.clear();
        commands.clear();
        statusBarItems.clear();
        settingsPanels.clear();
        menuProviders.clear();
        unitConfigActions.clear();
        virtualPackageProviders.clear();
        notify();
    },
};
